import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

// Core
import { ScreenMapData } from 'app/core/data/screen-map-data';

// Shared
import { BoundingBox } from 'app/shared/data/bounding-box';
import { RandoEvent } from 'app/core/data/rando-event';
import { Webcam } from 'app/shared/data/webcam';
import { MapLayer } from 'app/shared/data/map-layer';
import { PJ } from 'app/shared/data/pj';
import { RandonneursService } from 'app/shared/services/randonneurs.service';
import { Image } from 'app/shared/data/image';
  

@Injectable()
export class Mediator {

    private observers: MediatorListener[] = [];
    private screenData: ScreenMapData = new ScreenMapData();

    constructor(
        protected randonneursService: RandonneursService
      ) {
          //this.update(); // Initial update
    }


    /** Client subscribing for future updates */
    public subscribeForUpdates(observer: MediatorListener) {
        this.observers.push(observer);
    }

    /** Notify observers that the mediator's data has been updated */
    private notifyObservers(event) {
        console.log("Mediator => notifyObservers", event);
        for (let i=0; i<this.observers.length; i++) {
            this.observers[i].onMediatorUpdate(event);
        }      
    }      

    /** Force update of local data. Will then call observers */
    public update() {
        this.randonneursService.getPJ(this.screenData.boundingBox).subscribe(v=> {
            let layer = <MapLayer>v;
            layer.selected  = this.screenData.mapLayers["pj"].selected;
            this.screenData.mapLayers["pj"] = layer;
            this.notifyObservers(RandoEvent.build("PJ_UPDATED", this.screenData));
        });      
        this.randonneursService.getWebcams(this.screenData.boundingBox).subscribe(v=> {
            let layer = <MapLayer>v;
            layer.selected  = this.screenData.mapLayers["webcam"].selected;
            this.screenData.mapLayers["webcam"] = layer;
            this.notifyObservers(RandoEvent.build("CAM_UPDATED", this.screenData));  
        });               
        this.randonneursService.getImages(this.screenData.boundingBox).subscribe(v=> {
            let layer = <MapLayer>v;
            layer.selected  = this.screenData.mapLayers["image"].selected;
            this.screenData.mapLayers["image"] = layer;
            this.notifyObservers(RandoEvent.build("IMG_UPDATED", this.screenData));  
        });        
        this.randonneursService.getObjects(this.screenData.boundingBox).subscribe(v=> {
            let layer = <MapLayer>v;
            layer.selected  = this.screenData.mapLayers["robject"].selected;
            this.screenData.mapLayers["robject"] = layer;
            this.notifyObservers(RandoEvent.build("ROBJECT_UPDATED", this.screenData));  
        });        
    }

    public updateLayerSelection(pjSelected:boolean, camSelected:boolean, imgSelected:boolean, robjectSelected:boolean) {
        this.screenData.mapLayers["pj"].selected=pjSelected;
        this.screenData.mapLayers["webcam"].selected=camSelected;
        this.screenData.mapLayers["image"].selected=imgSelected;
        this.screenData.mapLayers["robject"].selected=robjectSelected;
        this.notifyObservers(RandoEvent.build("LAYER_SELECTED", this.screenData));
    }

    public updateBBox(boundingBox: BoundingBox) {
        this.screenData.boundingBox = boundingBox;        
        this.update();
        this.notifyObservers(RandoEvent.build("MAP_DATA_UPDATED", this.screenData));        
    }

    public getScreenData(): ScreenMapData {
        return this.screenData;
    }

    /** Get Data:PJ by ID */
    public getPJById(pjId:number):Observable<PJ> {
        return this.randonneursService.getPJById(pjId);
    }

    /** Get Data:PJ by ID */
    public getWebcamById(id:number):Observable<Webcam> {
        return this.randonneursService.getWebcamById(id);
    }

    /** Get Data:PJ by ID */
    public getImageById(imgId:string):Observable<Image> {
        return this.randonneursService.getImageById(imgId);
    }

    notify(messageId:string, type:string, id:string) {
        console.log("Mediator got notification:", messageId, type, id);
        // Map click -> get data, ask for overlay
        if (messageId=="MAP_CLICK") {
            if (type=="pj") {
                this.randonneursService.getPJById(Number(id)).subscribe(pj=> {
                    let data={
                        'lon': Number(pj.longitude),
                        'lat': Number(pj.latitude),
                        'title': pj.label + (pj.altitude>0?" ("+pj.altitude+"m)":""),
                        'img': 'https://www.randonneurs.ch/services/pj/'+pj.tmbImg,
                        'imgLink': 'https://www.randonneurs.ch/services/pj/'+pj.bigImg,
                        'body': pj.content
                    }
                    this.notifyObservers(RandoEvent.build("MAP_OVERLAY", data));
                });
            } else if (type=="webcam") {
                this.randonneursService.getWebcamById(Number(id)).subscribe(webcam=> {
                    let data={
                        'lon': Number(webcam.longitude),
                        'lat': Number(webcam.latitude),
                        'title': webcam.label + (webcam.altitude>0?" ("+webcam.altitude+"m)":""),
                        'img': webcam.urlimage,
                        'imgLink': webcam.urlimage,
                        'body': ''//'Source:' + webcam.urlsource + ", topic=" + webcam.urltopic
                    }
                    this.notifyObservers(RandoEvent.build("MAP_OVERLAY", data));
                });
            } else if (type=="image") {
                this.randonneursService.getImageById(id).subscribe(image=> {
                    let data={
                        'lon': Number(image.longitude),
                        'lat': Number(image.latitude),
                        'title': image.title,
                        'img': 'https://photoblog.randonneurs.ch/'+(''+image.id).replace('_', '/tmb'),
                        'imgLink': 'https://photoblog.randonneurs.ch/'+(''+image.id).replace('_', '/big'),
                        'body': image.datetime
                    }
                    this.notifyObservers(RandoEvent.build("MAP_OVERLAY", data));
                });
            } else if (type=="robject") {
                this.randonneursService.getObjectById(id).subscribe(robject=> {
                    let data={
                        'lon': Number(robject.longitude),
                        'lat': Number(robject.latitude),
                        'title': robject.title,
                        'img': '',
                        'imgLink': robject.url,
                        'body': robject.text
                    }
                    this.notifyObservers(RandoEvent.build("MAP_OVERLAY", data));
                });
            }
        }        
    }

}


/** Interface for mediator data observers */
export interface MediatorListener {
    onMediatorUpdate(event: RandoEvent);
}


// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/randonneurs-2018-build-and-to-local/src/app/mediator.ts